import GenericAssetView from "../views/generic-asset-view";
import HandleView from "../views/handle-view";
import camelToSnake from "../json/camel-to-snake";
import traverse from "./traverse-generator";
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter';
import { Group, Material, Mesh, MeshBasicMaterial, Object3D } from "three";
import { shimLegacyMaterials } from "./temporary-shim-legacy-materials";

/**
 * Please pass a *temporary* scene for export. This procedure will remove a
 * bunch of stuff from the scene.
 *
 * Note that this leaves the WebGL lights intact.
 */
export const exportForRender = async (scene: Object3D) => {
    // All the factory materials are replaced by 
    let placeholder = new MeshBasicMaterial({color: 0xFFFFFF});
    placeholder.name = 'Placeholder Material';

    // The userData of the scene itself is not exported, so the assetId of the
    // environment is stashed in an empty object.
    if(scene.userData.cpsEnvironmentAssetId) {
        const obj = new Group();
        obj.userData.cpsEnvironmentAssetId = scene.userData.cpsEnvironmentAssetId;
        scene.add(obj);
    }

    for(const obj of traverse(scene)) {
        // For generic assets that use an assetId, we trim the children, which
        // means we do not encode the object's data in the resulting GLB file
        // since it will be available on the server as a .blend file.
        if(obj instanceof GenericAssetView && obj.model.assetId != null) {
            obj.children = [];
            obj.userData.cpsAssetId = obj.model.assetId;
        }
        else if(obj instanceof HandleView) {
            // The handle model is not positioned exactly at the view. We reach
            // into the handle model and add the cpsAssetId property to its
            // `handle` child
            const materials: Material[] = [];
            collectMaterials(materials, obj.handle);
            const encoding = encodeMaterials(materials);

            // TODO: determine if we'll ever have handles without an asset ID
            // for all materials. We may not need this condition.
            //
            // If we are using any unsupported legacy materials, then we'll
            // send the handle as-is instead of trying to pare it down to use
            // the server asset.
            if(!encoding.includes(null)) {
                obj.userData.cpsMaterials = encoding;

                obj.children = [obj.handle];
                obj.handle.children = [];
                obj.handle.userData.cpsAssetId = obj.model.assetId;
            }
        }
        // Otherwise if this is a mesh, we can remove its materials and encode
        // them in the user data to be replaced on the server by optimized
        // Cycles materials that have the same specifications
        else if(obj instanceof Mesh) {
            const encoding = encodeMaterials(obj.material);
            // If any of the encoding failed for some reason, we leave all the
            // materials as-is so they should render reasonably well.
            if(encoding.includes(null)) {
                if(Array.isArray(obj.material)) {
                    obj.material.forEach(m => m.userData = camelToSnake(m.userData));
                } else {
                    obj.material.userData = camelToSnake(obj.material.userData)
                }
            } else {
                obj.userData.cpsMaterials = encoding;
                obj.material = placeholder;
            }
        }

        obj.userData = camelToSnake(obj.userData);
    }

    return await toGlbFile(scene);
}
export default exportForRender;


/**
 * Collect all the materials in a tree of objects. Note that this preserves
 * the ordering of the child objects which is important for maintaining the
 * correspondence between GLB meshes and Blender material slots.
 */
export const collectMaterials = (materials: Material[], ...objs: Object3D[]) => {
    for(let obj of objs) {
        if(obj instanceof Mesh) {
            if(Array.isArray(obj.material)) {
                materials.push(...obj.material);
            } else {
                materials.push(obj.material);
            }
        }
        if(obj.children.length > 0) {
            collectMaterials(materials, ...obj.children)
        }
    }
}

/**
 * Normalizes three.js's weird `material` property that can sometimes be an
 * array to always be an array, often of length one.
 */
export const encodeMaterials = (arg: Material | Material[]) => {
    if(Array.isArray(arg)) {
        return arg.map(encodeSingleMaterial);
    } else {
        return [encodeSingleMaterial(arg)];
    }
}

const encodeSingleMaterial = (mat: Material): Record<string, any> => {
    if(mat.userData.materialAssetId) {
        return {
            assetId: mat.userData.materialAssetId,
            color: `color-maps/${mat.userData.materialColormap}`,
            tint: mat.userData.materialTint,
        };
    } else {
        return shimLegacyMaterials(mat);
    }
};

/**
 * Used as a subroutine of exportForRender, handling just the GLTFExporter call.
 */
export const toGlbFile = (obj: Object3D): Promise<ArrayBuffer> => {
    return new Promise((resolve, reject) => {
        let exporter = new GLTFExporter();
        exporter.parse(
            obj,
            (glb: ArrayBuffer) => resolve(glb),
            error => reject(error),
            { binary: true }
        );
    });
}

